<template>
    <div class="map-overlay">
        <div v-if="isStreetView" class="close-button">
            <md-button class="md-white md-icon-button" :md-ripple="false" @click="close">
                <md-icon>close</md-icon>
            </md-button>
        </div>
        <MapSwitchButton />
        <div id="map_canvas" :class="isTrialExpired ? 'not-paid-map-overlay' : 'trip-map'" />
        <InfoWindow :is-date-today="isDateToday" v-if="!isStreetView" />
        <div v-if="activePanel === 'Team Members'">
            <DriverMarker
                v-for="driver in driverDetails"
                :key="driver.publicUserId"
                :driver="driver"
                :markers="driverMarkers"
                @deleteMarker="handleDeleteDriverMarker"
                :is-date-today="isDateToday"
            >
                <template
                    slot-scope="{ publicUserId }"
                    v-if="stopDetails[publicUserId] && stopDetails[publicUserId].length > 0"
                >
                    <div>
                        <StopMarker
                            v-for="stop in getModifiedStops(stopDetails, publicUserId)"
                            :key="stop.stopId"
                            :public-user-id="publicUserId"
                            :stop="stop"
                            :driver="driver"
                            :markers="stopMarkers"
                            :stop-order="stop.stopIndex"
                            :min-distance="minDistance"
                            @deleteMarker="handleDeleteStopMarker"
                        ></StopMarker>
                    </div>
                </template>
            </DriverMarker>
        </div>
        <div v-else>
            <AssetGroupMarker
                v-for="assetGroup in assetGroupDetails"
                :key="assetGroup.assetGroupId"
                :asset-group="assetGroup"
                :markers="assetMarkers"
                @deleteMarker="handleDeleteAssetGroupMarker"
                :is-date-today="isDateToday"
            />
        </div>

        <RoutePolyline :pinned-driver-details="pinnedDriverDetails" :is-date-today="isDateToday" />
        <TeamMembersList
            v-if="membersLoaded && (canViewTeamRegion || isReadOnlyUser) && !isStreetView"
            :drivers="driverDetails"
            :render-key="renderKey"
            :asset-groups="assetGroupDetails"
            @changeLoading="(val) => (isLoading = val)"
        />
        <div v-if="isLoading">
            <div class="map-loader">
                <fade-loader :loading="isLoading" color="#000" />
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable no-restricted-properties */
import { DATE_TYPES } from '@/utils/constants';
import { MapBoxMixin } from '@/mixins/MapBoxMixin';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import { ref, provide, reactive, computed, toRefs, inject, onMounted, onBeforeUnmount } from '@vue/composition-api';
import moment from 'moment';
import FadeLoader from 'vue-spinner/src/FadeLoader';

import {
    showErrorMessage,
    handleRequests,
    getDistanceBetweenMarkers,
    filterMembersByTeamRegionId,
    getRegionCoordinates
} from '@/helpers';
import { MarkerPins } from '@/utils/MarkerPins';

import DriverMarker from './components/mapbox/DriverMarker';
import AssetGroupMarker from './components/mapbox/AssetGroupMarker';
import StopMarker from './components/mapbox/StopMarker';
import RoutePolyline from './components/mapbox/RoutePolyline';
import InfoWindow from './components/mapbox/InfoWindow';
import TeamMembersList from './components/mapbox/TeamMembersList';
import MapSwitchButton from './components/mapbox/MapSwitchButton';

export default {
    name: 'MapOverviewMapBox',
    mixins: [MapBoxMixin, GeneralMixin],
    components: {
        FadeLoader,
        DriverMarker,
        AssetGroupMarker,
        RoutePolyline,
        TeamMembersList,
        InfoWindow,
        MapSwitchButton,
        StopMarker
    },
    setup(_, { root }) {
        const membersLoaded = ref(false);
        const isLoading = ref(false);
        const { getters, commit } = inject('vuex-store');
        const userHasTeamAccess = getters['user/hasTeamAccess'];
        const userHasAssetAccess = getters['user/hasAssetAccess'];
        const publicUserId = getters['user/publicUserId'];
        const hasElevatedTeamRegionFilter = computed(() => getters['user/hasElevatedTeamRegionFilter']);
        const canViewTeamRegion = computed(() => getters['user/canViewTeamRegion']);
        const teamRegionId = computed(() => getters['map/teamRegionId']);
        const teamRegions = computed(() => getters['team/teamRegions']);
        const allTeamMembers = computed(() => getters['team/teamMembers']);
        const useNewMap = getters['map/useNewMap'];
        const filteredTeamMembers = computed(() => {
            return filterMembersByTeamRegionId(
                teamRegions.value,
                allTeamMembers.value,
                null,
                teamRegionId.value,
                false,
                true
            );
        });
        const userIdParam = ref(null);
        const loadUserParam = ref(false);
        const selectedTeamRegionId = ref(null);
        const mapUrl = ref(useNewMap ? '/map2' : '/map');

        const state = reactive({
            driverDetails: [],
            assetGroupDetails: [],
            stopDetails: {},
            geofenceData: {},
            pinnedDriverDetails: {},
            geofenceList: [],
            driverBreakList: []
        });

        const getModifiedStops = (stops, id) => {
            let counter = 0;
            return stops[id].map((stop) => {
                if (!['complete', 'cancelled', 'failed', 'on hold'].includes(stop.status.toLowerCase())) {
                    stop.stopIndex = counter;
                    counter += 1;
                }
                return stop;
            });
        };

        const bounds = ref(null);
        const map = ref(null);
        const isStreetView = ref(false);

        const date = computed(() => getters['map/date']);
        const pinnedUser = computed(() => getters['map/pinnedUser']);
        const currentUser = computed(() => getters['user/user']);
        const locationHistoryDetails = computed(() => getters['map/timeseriesData']);
        const activePanel = computed(() => getters['map/activePanel']);
        const clickedStop = computed(() => getters['map/clickedStop']);
        const driverBreakList = computed(() => getters['map/driverBreakList']);
        const isReadOnlyUser = computed(() => getters['user/isReadOnlyUser']);
        const zoomLevel = ref(0);
        const minDistance = ref(0);

        // Set Team Members tab if page redirect from the trip
        if (root.$route.query?.from === 'trip') {
            commit('map/CHANGE_ACTIVE_PANEL', 'Team Members');
        }

        const isDateToday = ref(new Date(date.value).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0));

        const close = () => {};

        const renderKey = ref(0);

        const getUser = async (publicUserId) => {
            if (publicUserId) {
                const url = `/api/team-members/${publicUserId}`;
                await handleRequests(url).then((data) => {
                    if (data.data && data.data.data) {
                        commit(
                            'map/SET_TEAM_REGION_ID',
                            data.data.data.teamRegionId ? data.data.data.teamRegionId : -1
                        );

                        selectedTeamRegionId.value = data.data.data.teamRegionId ? data.data.data.teamRegionId : -1;
                    }
                });
            }
        };

        onMounted(async () => {
            let date = new Date();
            // we need to format the given date of the url to a valid date string accepted by the Date object
            if (root.$route.params.date) {
                date = new Date(root.$route.params.date);
            }
            commit('map/CHANGE_DATE', { date });
            // check if has user id (directly accessed) to set the correct teamRegionId later
            if (root.$route.params.userId && activePanel.value === 'Team Members') {
                userIdParam.value = root.$route.params.userId;
                loadUserParam.value = true;

                // set selected TeamRegionId to incoming Driver's TR
                await getUser(userIdParam.value);
            } else {
                commit('map/SET_TEAM_REGION_ID', null); // All
                selectedTeamRegionId.value = null; // All set // currentUser.value.teamRegionId ? currentUser.value.teamRegionId : -1; // not set
            }
        });

        onBeforeUnmount(() => {
            // destroy instance
            map.value = null;
            bounds.value = null;
            state.driverdetails = [];
            state.assetGroupDetails = [];
            state.stopDetails = {};
            state.driverBreakList = [];

            commit('map/UNPIN_DRIVER');
        });

        // provide('google', google);
        provide('map', map);
        provide('bounds', bounds);
        provide('isStreetView', isStreetView);

        return {
            isStreetView,
            close,
            isLoading,
            membersLoaded,
            ...toRefs(state),
            zoomLevel,
            userHasTeamAccess,
            minDistance,
            locationHistoryDetails,
            activePanel,
            pinnedUser,
            currentUser,
            isDateToday,
            renderKey,
            hasElevatedTeamRegionFilter,
            canViewTeamRegion,
            getModifiedStops,
            isReadOnlyUser,
            teamRegionId,
            getUser,
            map,
            teamRegions,
            selectedTeamRegionId,
            driverBreakList,
            userHasAssetAccess,
            filteredTeamMembers,
            publicUserId,
            date,
            state,
            clickedStop,
            mapUrl
        };
    },
    computed: {
        ...mapGetters({
            isTrialExpired: 'team/isTrialExpired'
        })
    },
    data() {
        return {
            drawingManager: null,
            drawnShapeStops: [],
            skillOptions: [],
            assetMarkers: [],
            driverMarkers: [],
            stopMarkers: []
        };
    },
    watch: {
        async date(newVal) {
            await this.fetchData(newVal);
        },
        activePanel(newVal) {
            if (newVal === 'Asset Groups') {
                // Clear Driver markers
                this.driverMarkers.forEach((marker) => {
                    this.$_mapBox_removeElement(marker);
                });

                // Clear stop markers
                this.stopMarkers.forEach((marker) => {
                    this.$_mapBox_removeElement(marker);
                });

                this.driverMarkers = [];
                this.stopMarkers = [];
            } else if (newVal === 'Team Members') {
                this.assetMarkers.forEach((element) => {
                    this.$_mapBox_removeElement(element);
                });
                this.assetMarkers = [];
            }
        },
        async pinnedUser(newVal) {
            if (this.activePanel === 'Team Members') {
                this.state.pinnedDriverDetails = this.state.driverDetails.find((d) => d.publicUserId === newVal);
            } else {
                this.state.pinnedDriverDetails = this.state.assetGroupDetails.find(
                    (a) => a.trackerReference === newVal
                );
            }

            if (this.geofenceConfiguration === 'TEST') {
                const url = `/api/geofence?tripDate=${moment(this.date).format('YYYY-MM-DD')}&publicUserId=${newVal}`;

                const {
                    data: { data: geofenceData }
                } = await handleRequests(url);

                this.state.geofenceData = geofenceData.reduce((obj, item) => {
                    if (obj[item.stopId]) {
                        obj[item.stopId].push(item);
                    } else {
                        obj[item.stopId] = [item];
                    }
                    return obj;
                }, {});
            }
        },
        clickedStop() {}
    },
    mounted() {
        this.initializeMap();
    },
    methods: {
        initializeMap() {
            // get starting location of user. if there is none, default to Australia
            const { latitude, longitude } = this.currentUser.startLocation
                ? this.currentUser.startLocation
                : getRegionCoordinates('AU');

            // map
            this.map = this.$_mapBox_createMap('map_canvas', latitude, longitude, null, this.callRoutingInterval);
            this.drawingManager = this.$_map_drawingManager();
            this.map.addControl(this.drawingManager);
            this.$_mapBox_handleListener(this.map, 'bounds_changed', () => {
                this.zoomLevel = this.map.getZoom();
                const mapCenter = this.map.getCenter();
                // get minimum distance to show stop pins.
                const distanceScale =
                    (156543.03392 * Math.cos((mapCenter.lat() * Math.PI) / 180)) / Math.pow(2, this.zoomLevel) / 1000; // distance per pixel in km at a particular zoom level
                this.minDistance = distanceScale * 50; // half of the size of the marker in pixels. if the distance is less than this, the markers will overlap
            });

            // bounds
            this.bounds = this.$_mapbox_createBounds(this.map);

            this.drawnShapeStops = [];
            this.skillOptions = this.currentUser.skillOptions;
        },
        async handleTeamRegionChanged(args) {
            const prevTeamRegionId = this.selectedTeamRegionId;
            this.$store.commit('map/SET_TEAM_REGION_ID', args.teamRegionId);

            if (prevTeamRegionId !== args?.teamRegionId) {
                this.unpinUser();
            }

            await this.fetchData(this.date);
        },
        unpinUser() {
            const { params } = this.$router.currentRoute;
            if (moment(params.date).isSame(moment(), 'day')) {
                this.$router.replace({ path: `${this.mapUrl}` });
            } else {
                this.$router.replace({ path: `${this.mapUrl}/${params.date}` });
            }
            this.$store.commit('map/UNPIN_DRIVER');
        },
        async fetchData(dateToLoad) {
            this.isDateToday = new Date(dateToLoad).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
            this.$store.commit('map/CLEAR_TIMESERIES_DATA');
            await this.fetchMapData();
            // if there is a pinnedUser, fetch his details
            if (this.pinnedUser) {
                this.isLoading = true;
                this.$store.dispatch('map/PIN_DRIVER', {
                    userId: this.pinnedUser,
                    date: this.$root.$options.filters.dateFormat(dateToLoad, DATE_TYPES.internationalDate),
                    isAssetGroup: this.activePanel === 'Asset Groups'
                });

                this.$store
                    .dispatch('map/FETCH_TIMESERIES_DATA', {
                        userId: this.pinnedUser,
                        tripDate: moment(this.date).format('YYYY-MM-DD'),
                        isTracker: this.activePanel === 'Asset Groups'
                    })
                    .then(() => {
                        this.isLoading = false;
                    });
            }
        },
        async fetchMapData() {
            this.isLoading = true;
            this.state.driverdetails = [];
            this.state.assetGroupDetails = this.state.assetGroupDetails || [];
            this.state.stopDetails = {};
            this.state.driveBreakDetails = {};

            const fromDate = moment(this.date).format('YYYY-MM-DD');
            if (this.userHasAssetAccess || this.isReadOnlyUser) {
                // fetch asset groups data
                await this.$store
                    .dispatch('assetGroup/FETCH_ASSET_GROUPS_LOCATION_DETAILS', {
                        teamRegionId: this.teamRegionId,
                        elevatedTeamRegionFilter: this.hasElevatedTeamRegionFilter,
                        date: fromDate
                    })
                    .catch((e) => {
                        const message = 'There was an error in fetching the asset group details';
                        showErrorMessage(this, message, e);
                    })
                    .then((r) => {
                        this.state.assetGroupDetails = r.map((a, i) => ({
                            ...a,
                            markerPins: MarkerPins[i % MarkerPins.length],
                            isAssetGroup: true
                        }));
                    });
            }

            // fetch team member data
            await this.$store
                .dispatch('team/FETCH_TEAM_MEMBERS', {
                    date: fromDate,
                    includeLocationDetails: true,
                    teamRegionId: this.teamRegionId,
                    strictTeamRegionFilter: false, // true
                    elevatedTeamRegionFilter: this.hasElevatedTeamRegionFilter
                })
                .catch((e) => {
                    const message = 'There was an error in fetching the driver details.';
                    showErrorMessage(this, message, e);
                })
                .then(() => {
                    this.state.driverDetails = this.filteredTeamMembers.map((d, i) => ({
                        ...d,
                        markerPins: MarkerPins[i % MarkerPins.length],
                        isAssetGroup: false
                    }));

                    // Virtually pin the user, if induvidual user
                    if (
                        this.state.driverDetails.length === 1 &&
                        (this.currentUser.publicUserId || this.isReadOnlyUser) &&
                        this.activePanel !== 'Asset Groups'
                    ) {
                        this.$store.dispatch('map/PIN_DRIVER', {
                            userId: this.state.driverDetails[0].publicUserId
                        });
                    }
                    this.membersLoaded = true;
                });

            // fetch stops data
            const userId = this.userHasTeamAccess ? '' : `&userId=${this.publicUserId}`;

            // removed teamRegion query param because for some scenario a Stop may have a TR but it's assigned to a member w/o TR
            // and so, if there's a mismatch between Stop and member's TR, no Stop will be returned
            const stopDetailsEndpoint = `/api/stops?fromDate=${fromDate}${userId}&includeStatusHistory=true`;
            await this.$store
                .dispatch('map/FETCH_STOPS', { endpoint: stopDetailsEndpoint })
                .catch((e) => {
                    const message = 'There was an error in fetching the stops.';
                    showErrorMessage(this, message, e);
                })
                .then((r) => {
                    const stopsObj = {};
                    // loop through all stops to put distance between stops
                    Object.entries(r).forEach(([userId, stops]) => {
                        // users with more than two stops
                        if (stops.length >= 2) {
                            stops.forEach((stop, index) => {
                                if (index > 0) {
                                    const { location: firstStopLoc } = stop;
                                    const { location: secondStopLoc } = stops[index - 1];
                                    stop.distance = getDistanceBetweenMarkers(firstStopLoc, secondStopLoc);
                                    // make first stop the same distance as second stop
                                    if (index === 1)
                                        stops[0].distance = getDistanceBetweenMarkers(firstStopLoc, secondStopLoc);
                                }
                            });
                        }

                        stopsObj[userId] = stops;
                    });
                    this.state.stopDetails = stopsObj;
                });

            this.isLoading = false;
        },
        handleDeleteStopMarker(marker) {
            if (marker && this.stopMarkers.length > 0) {
                const stopMarker = this.stopMarkers.find((x) => x.stopId === marker.stopId);
                if (!stopMarker) {
                    return;
                }
                this.stopMarkers = this.stopMarkers.filter((x) => x.stopId !== stopMarker.stopId);
                this.$_mapBox_removeElement(stopMarker);
            }
        },
        handleDeleteAssetGroupMarker(marker) {
            if (marker && this.assetMarkers.length > 0) {
                const assetMarker = this.assetMarkers.find((x) => x.trackerReference === marker.trackerReference);
                if (!assetMarker) {
                    return;
                }
                this.assetMarkers = this.assetMarkers.filter(
                    (x) => x.trackerReference !== assetMarker.trackerReference
                );
                this.$_mapBox_removeElement(marker);
            }
        },
        handleDeleteDriverMarker(marker) {
            if (marker && this.driverMarkers.length > 0) {
                const driverMarker = this.driverMarkers.find((x) => x.userId === marker.userId);
                if (!driverMarker) {
                    return;
                }
                this.driverMarkers = this.driverMarkers.filter((x) => x.userId !== driverMarker.userId);
                this.$_mapBox_removeElement(driverMarker);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.map-overlay {
    position: relative;
    width: 100%;

    .not-paid-map-overlay {
        width: 100%;
        height: calc(100vh - 60px);

        > div {
            bottom: 0;
        }
    }

    .trip-map {
        width: 100%;
        height: 100vh;
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    ::v-deep .asset-map-image-marker {
        background-color: var(--driver-color);
        border-radius: 50%;
        cursor: pointer !important;
        height: 40px;
        margin-left: -20px;
        /* margin-left = -width/2 */
        margin-top: -50px;
        /* margin-top = -height + arrow */
        padding: 0px;
        position: absolute;
        width: 40px;

        &:after {
            border-color: var(--driver-color) transparent;
            border-style: solid;
            border-width: 10px 10px 0;
            bottom: -8px;
            content: '';
            display: block;
            left: 10px;
            position: absolute;
            width: 0;
        }

        .image {
            background-color: var(--driver-color);
            background-position: center center;
            background-size: cover;
            border-radius: 50%;
            height: 36px;
            margin: 2px;
            width: 36px;

            &.asset {
                background-size: auto;
                width: 87%;
                background-position: 34% 25%;
            }
        }
    }

    .map-loader {
        position: absolute;
        top: 35%;
        left: 50%;

        span {
            position: absolute;
            margin-top: 50px;
            width: 110px;
            left: calc((100% - 100px) / 2);
            text-align: center;
            font-weight: 600;
        }
    }

    .close-button {
        position: absolute;
        right: 15px;
        top: 15px;
        z-index: 4;

        .md-button {
            .md-icon {
                color: black !important;
            }
        }
    }

    .team-region-container {
        position: absolute;
        top: 10px;
        left: 405px;
        z-index: 2;
        height: 40px;
        background: white;
        max-width: 218px;
        padding: 0 8px;
        display: flex;
        border-radius: 3px;

        ::v-deep .vs__dropdown-toggle {
            border: 0;
            margin-top: -9px;
            padding-bottom: 1px;
        }
    }

    .map-calendar {
        width: 155px;
    }
    .marker-stop {
        background-size: cover;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        cursor: pointer;
    }

    ::v-deep .marker-stop {
        width: 0;
        height: 0;
    }

    ::v-deep .marker-stop span {
        display: flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        width: 30px;
        height: 30px;
        color: white;
        border: solid 2px;
        border-radius: 0 70% 70%;
        box-shadow: 0 0 2px #000;
        cursor: pointer;
        transform-origin: 0 0;
        transform: rotateZ(-135deg);

        img {
            transform: rotateZ(135deg);
            border-radius: 50%;
        }
    }

    ::v-deep .marker-stop b {
        transform: rotateZ(135deg);
        color: #fff;
    }
}
</style>
